import { useGetClientsPaginated } from "@/hooks/clients/use-clients";
import {
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  MRT_ToggleFiltersButton,
} from "material-react-table";
import { useMemo } from "react";

import { APIPaginatedTable } from "@/components/common/api-paginated-table";
import type { APIPaginatedTableState } from "@/components/common/api-paginated-table/states";
import type { Client } from "@/types/clients/api-types/client-types";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";

const createClientsFilters = (columnFilters: MRT_ColumnFiltersState) => {
  const filters: {
    first_name?: string;
    last_name?: string;
  } = {};

  const getTrimmedString = (value: unknown) =>
    typeof value === "string" ? value.trim() : "";

  columnFilters.forEach((filter) => {
    const colName = filter.id.split(".")[1] ?? filter.id;
    const trimmedValue = getTrimmedString(filter.value);
    if (trimmedValue === "") return;

    if (colName === "first_name") {
      filters.first_name = trimmedValue;
    }
    if (colName === "last_name") {
      filters.last_name = trimmedValue;
    }
  });

  return filters;
};

interface ClientsTableProps {
  onClientClick: (client: Client) => void;
  onDeleteClick?: (client: Client) => void;
  showActions?: boolean;
}

const ClientsTable = ({
  onClientClick,
  onDeleteClick,
  showActions = true,
}: ClientsTableProps) => {
  const columns = useMemo<MRT_ColumnDef<Client>[]>(() => {
    const baseColumns: MRT_ColumnDef<Client>[] = [
      {
        accessorKey: "first_name",
        header: "First Name",
        Cell: ({ row, renderedCellValue }) => {
          const client = row.original;
          return (
            <button
              type="button"
              onClick={() => onClientClick(client)}
              className="text-purple-500 hover:underline cursor-pointer"
            >
              {renderedCellValue}
            </button>
          );
        },
      },
      {
        accessorKey: "last_name",
        header: "Last Name",
      },
    ];

    if (!showActions || !onDeleteClick) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        id: "actions",
        header: "Actions",
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const client = row.original;
          return (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onDeleteClick(client)}
            >
              <Trash2 />
            </Button>
          );
        },
      },
    ];
  }, [onClientClick, onDeleteClick, showActions]);

  return (
    <APIPaginatedTable
      columns={columns}
      reactQuery={useGetClientsPaginated}
      reactQueryFilters={(state: APIPaginatedTableState) => {
        const filters = createClientsFilters(state.columnFilters);

        return {
          page: state.pagination.pageIndex,
          size: state.pagination.pageSize,
          ...filters,
        };
      }}
      onRenderToolbarInternalActions={({ table }) => (
        <MRT_ToggleFiltersButton table={table} />
      )}
      titleTopic="clients"
      contentTopic="client"
    />
  );
};

export default ClientsTable;
